/**
 * Busy event content renderer for the manual calendar.
 * Shows interviewer / meeting-room busy time blocks with the event
 * subject. Hover reveals a tooltip carrying the full title + time
 * range so truncated titles remain discoverable.
 *
 * Ref: KInterviewScheduler/.../BusyEventContent.tsx
 */
import { Tooltip } from '@chakra-ui/react';
import moment from 'moment-timezone';
import { getEventPadding, calcDurationMinutes } from './eventHelpers';

interface BusyEventContentProps {
  title?: string;
  /** Override the tooltip headline (meeting rooms use this to show
   *  "RoomName · MeetingSubject" while the block itself stays concise). */
  tooltipTitle?: string;
  /** Free (show-as-free) event — appends a "(Free)" label to the tooltip
   *  heading so hover distinguishes it from a busy block. */
  isFree?: boolean;
  start: string;
  end: string;
  timezone?: string;
  /**
   * When provided, clicking the busy block calls this handler with the
   * native mouse event. The caller is responsible for Y-position time
   * calculation and slot creation. `e.stopPropagation()` is called
   * automatically so FullCalendar's eventClick does not double-fire.
   */
  onSlotClick?: (_e: React.MouseEvent<HTMLDivElement>) => void;
}

function formatRange(start: string, end: string, timezone?: string): string {
  const tz = timezone || moment.tz.guess();
  const s = moment.tz(start, tz);
  const e = moment.tz(end, tz);
  if (!s.isValid() || !e.isValid()) return '';
  // Cross-day events (e.g. all-day OOO Mon 9:30 PM → Tue 9:30 PM) need
  // both dates in the label — otherwise the times collapse to
  // "9:30 PM – 9:30 PM" and the event reads as zero duration.
  if (!s.isSame(e, 'day')) {
    return `${s.format('ddd, MMM D, h:mm A')} – ${e.format('ddd, MMM D, h:mm A')}`;
  }
  const date = s.format('ddd, MMM D');
  return `${date} · ${s.format('h:mm A')} – ${e.format('h:mm A')}`;
}

/** Short inline time label for the in-event row. Mirrors the tooltip's
 *  cross-day handling but uses a compact "Mon 9:30 PM – Tue 9:30 PM"
 *  form so the per-segment block doesn't read as zero duration. */
function formatInlineTimes(
  start: string,
  end: string,
  timezone?: string
): string {
  const tz = timezone || moment.tz.guess();
  const s = moment.tz(start, tz);
  const e = moment.tz(end, tz);
  if (!s.isValid() || !e.isValid()) return '';
  if (!s.isSame(e, 'day')) {
    return `${s.format('ddd h:mm A')} – ${e.format('ddd h:mm A')}`;
  }
  return `${s.format('h:mm A')} – ${e.format('h:mm A')}`;
}

export const BusyEventContent = ({
  title,
  tooltipTitle,
  isFree,
  start,
  end,
  timezone,
  onSlotClick,
}: BusyEventContentProps) => {
  const duration = calcDurationMinutes(start, end);
  const padding = getEventPadding(duration);
  const rangeLabel = formatRange(start, end, timezone);
  const tooltipLabel = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ fontWeight: 600 }}>
        {tooltipTitle || title || 'Busy'}
        {isFree && (
          <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 6 }}>
            (Free)
          </span>
        )}
      </div>
      {rangeLabel && <div style={{ opacity: 0.85 }}>{rangeLabel}</div>}
    </div>
  );

  return (
    <Tooltip label={tooltipLabel} placement="top" hasArrow openDelay={120}>
      <div
        style={{
          width: '100%',
          height: '100%',
          padding,
          overflow: 'hidden',
          cursor: onSlotClick ? 'pointer' : 'default',
        }}
        onClick={
          onSlotClick
            ? (e) => {
                e.stopPropagation();
                onSlotClick(e);
              }
            : undefined
        }
      >
        {title && (
          <div
            style={{
              fontSize: '11px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'inherit',
            }}
          >
            {title}
          </div>
        )}
        {duration >= 45 && rangeLabel && (
          <div
            style={{
              fontSize: '10px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              opacity: 0.75,
              color: 'inherit',
            }}
          >
            {formatInlineTimes(start, end, timezone)}
          </div>
        )}
      </div>
    </Tooltip>
  );
};
